import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import InfoIcon from "@material-ui/icons/Info";
import WarningIcon from "@material-ui/icons/Warning";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

import { getActivePopups, dismissPopup } from "../../services/companyPopupService";

const TYPE_CONFIG = {
  info: {
    Icon: InfoIcon,
    headerBg: "#0ea5e9",
    iconColor: "#fff",
    badgeBg: "#e0f2fe",
    badgeColor: "#0369a1",
    label: "Informação",
  },
  warning: {
    Icon: WarningIcon,
    headerBg: "#f59e0b",
    iconColor: "#fff",
    badgeBg: "#fef9c3",
    badgeColor: "#92400e",
    label: "Atenção",
  },
  success: {
    Icon: CheckCircleIcon,
    headerBg: "#22c55e",
    iconColor: "#fff",
    badgeBg: "#dcfce7",
    badgeColor: "#166534",
    label: "Sucesso",
  },
  error: {
    Icon: ErrorIcon,
    headerBg: "#ef4444",
    iconColor: "#fff",
    badgeBg: "#fee2e2",
    badgeColor: "#991b1b",
    label: "Alerta",
  },
  announcement: {
    Icon: AnnouncementIcon,
    headerBg: "#8b5cf6",
    iconColor: "#fff",
    badgeBg: "#f3e8ff",
    badgeColor: "#7c3aed",
    label: "Novidade",
  },
};

const useStyles = makeStyles(() => ({
  popupHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 20px 12px",
    color: "#fff",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: "1.1rem",
    color: "#fff",
    lineHeight: 1.2,
  },
  headerBadge: {
    fontSize: "0.68rem",
    fontWeight: 700,
    padding: "2px 8px",
    borderRadius: 10,
    display: "inline-block",
    marginTop: 2,
    opacity: 0.85,
  },
  closeBtn: {
    color: "rgba(255,255,255,0.8)",
    "&:hover": { color: "#fff", background: "rgba(255,255,255,0.15)" },
  },
  coverImage: {
    width: "100%",
    maxHeight: 220,
    objectFit: "cover",
  },
  contentHtml: {
    fontSize: "0.95rem",
    lineHeight: 1.7,
    color: "#334155",
    "& p": { marginBottom: 8 },
    "& ul, & ol": { paddingLeft: 20, marginBottom: 8 },
    "& strong": { color: "#1e293b" },
    "& li": { marginBottom: 3 },
  },
  dialogPaper: {
    borderRadius: 14,
    overflow: "hidden",
    maxWidth: 520,
  },
  dialogContent: {
    padding: "16px 20px",
  },
  dialogActions: {
    padding: "8px 20px 16px",
    gap: 8,
    justifyContent: "flex-end",
  },
}));

const CompanyPopupDisplay = () => {
  const classes = useStyles();

  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Busca popups uma vez por sessão
    const fetched = sessionStorage.getItem("company_popups_fetched");
    if (fetched) return;
    sessionStorage.setItem("company_popups_fetched", "1");

    getActivePopups()
      .then(({ data }) => {
        if (data && data.length > 0) {
          setQueue(data);
          setCurrent(data[0]);
          setOpen(true);
        }
      })
      .catch(() => {});
  }, []);

  const advance = () => {
    const next = queue.slice(1);
    setQueue(next);
    if (next.length > 0) {
      setCurrent(next[0]);
      setOpen(true);
    } else {
      setCurrent(null);
      setOpen(false);
    }
  };

  const handleDismiss = async (popup) => {
    try {
      await dismissPopup(popup.id);
    } catch {}
    advance();
  };

  const handleClose = async (event, reason) => {
    if (!current) return;
    if (!current.dismissible && reason === "backdropClick") return;
    if (!current.dismissible && reason === "escapeKeyDown") return;
    await handleDismiss(current);
  };

  if (!current) return null;

  const cfg = TYPE_CONFIG[current.type] || TYPE_CONFIG.info;
  const { Icon } = cfg;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableBackdropClick={!current.dismissible}
      disableEscapeKeyDown={!current.dismissible}
      PaperProps={{ className: classes.dialogPaper }}
    >
      {/* Header colorido */}
      <div className={classes.popupHeader} style={{ background: cfg.headerBg }}>
        <div className={classes.headerLeft}>
          <Icon style={{ color: cfg.iconColor, fontSize: 28 }} />
          <div>
            <Typography className={classes.headerTitle}>{current.title}</Typography>
            <span className={classes.headerBadge}
              style={{ background: "rgba(255,255,255,0.2)", color: "#fff" }}>
              {cfg.label}
            </span>
          </div>
        </div>
        {current.dismissible && (
          <IconButton size="small" className={classes.closeBtn} onClick={() => handleDismiss(current)}>
            <CloseIcon />
          </IconButton>
        )}
      </div>

      {/* Imagem */}
      {current.imageUrl && (
        <img src={current.imageUrl} alt="" className={classes.coverImage} />
      )}

      {/* Conteúdo */}
      {current.content && (
        <DialogContent className={classes.dialogContent}>
          <div
            className={classes.contentHtml}
            dangerouslySetInnerHTML={{ __html: current.content }}
          />
        </DialogContent>
      )}

      {/* Ações */}
      <DialogActions className={classes.dialogActions}>
        {current.dismissible && (
          <Button
            size="small"
            style={{ textTransform: "none", color: "#94a3b8" }}
            onClick={() => handleDismiss(current)}
          >
            Fechar
          </Button>
        )}
        <Button
          variant="contained"
          size="small"
          style={{ background: cfg.headerBg, color: "#fff", textTransform: "none",
            fontWeight: 700, borderRadius: 8, padding: "6px 20px" }}
          endIcon={current.buttonUrl ? <OpenInNewIcon /> : undefined}
          onClick={() => {
            if (current.buttonUrl) window.open(current.buttonUrl, "_blank");
            handleDismiss(current);
          }}
        >
          {current.buttonText || "Entendi"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompanyPopupDisplay;
